// Shared API client used by both portals.
// Attaches Authorization: Bearer token from MSAL on every request.
// Reads X-Correlation-Id from responses for telemetry correlation.

export interface ApiError {
  type: string;
  title: string;
  status: number;
  detail: string;
  traceId: string;
  errors?: Record<string, string[]>;
}

/** Options passed to the portal-supplied token provider. */
export interface AccessTokenOptions {
  /**
   * Force MSAL to bypass its cached access token and mint a fresh one from the
   * refresh token / SSO session. The client's 401 retry path sets this so an
   * expired-but-renewable token self-heals instead of logging the user out.
   */
  forceRefresh?: boolean;
}

interface ApiClientOptions {
  baseUrl: string;
  getAccessToken: (opts?: AccessTokenOptions) => Promise<string>;
  getExtraHeaders?: () => Record<string, string>;
  /**
   * Called when a request is still 401 Unauthorized AFTER one transparent
   * token-refresh retry — i.e. the session genuinely cannot be renewed
   * silently. The portal wires this to redirect the user back to the sign-in
   * landing page. The client guarantees at-most-one invocation per "session of
   * 401s": once fired, further 401s are suppressed until a successful response
   * resets it, so a burst of concurrent expired-token calls triggers a single
   * redirect rather than many.
   */
  onUnauthorized?: () => void;
}

class ApiClient {
  private baseUrl: string;
  private getAccessToken: (opts?: AccessTokenOptions) => Promise<string>;
  private getExtraHeaders: () => Record<string, string>;
  private onUnauthorized?: () => void;
  private unauthorizedHandled = false;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.getAccessToken = options.getAccessToken;
    this.getExtraHeaders = options.getExtraHeaders ?? (() => ({}));
    this.onUnauthorized = options.onUnauthorized;
  }

  /** Fire the 401 handler at most once until a later success resets it. */
  private handleUnauthorized(): void {
    if (this.unauthorizedHandled) return;
    this.unauthorizedHandled = true;
    this.onUnauthorized?.();
  }

  /**
   * Issue an authenticated request, transparently recovering from an expired
   * access token. On a 401 we force-refresh the token and retry the request
   * exactly ONCE; only if the retry is still 401 (or no fresh token could be
   * obtained) do we fire onUnauthorized — which redirects to the login page.
   * So a token that expired while the app sat idle self-heals on the next call
   * instead of bouncing the user out on the first stale request.
   *
   * `buildInit` maps a bearer token to the fetch init for this request; it is
   * called again with the refreshed token on retry so each attempt carries its
   * own Authorization header.
   */
  private async sendAuthed(url: string, buildInit: (token: string) => RequestInit): Promise<Response> {
    const token = await this.getAccessToken();
    let response = await fetch(url, buildInit(token));

    if (response.status === 401) {
      const fresh = await this.getAccessToken({ forceRefresh: true });
      if (fresh && fresh !== token) {
        response = await fetch(url, buildInit(fresh));
      }
      // Still unauthorized after the retry, or nothing fresh to retry with:
      // the session cannot be renewed silently → redirect to sign-in.
      if (response.status === 401) {
        this.handleUnauthorized();
      }
    }

    return response;
  }

  private async parseError(response: Response): Promise<ApiError> {
    return response.json().catch(() => ({
      type: 'about:blank',
      title: response.statusText,
      status: response.status,
      detail: 'An error occurred',
      traceId: response.headers.get('X-Correlation-Id') ?? 'unknown',
    }));
  }

  async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const extraHeaders = this.getExtraHeaders();
    const response = await this.sendAuthed(`${this.baseUrl}${path}`, (token) => ({
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...extraHeaders,
        ...options.headers,
      },
    }));

    if (!response.ok) {
      throw await this.parseError(response);
    }

    // A successful response means the token is valid again — re-arm the
    // 401 handler so a later expiry can redirect.
    this.unauthorizedHandled = false;

    // NULL, never undefined. React Query v5 REJECTS an undefined queryFn result
    // ("Query data cannot be undefined") and surfaces it as a query ERROR, so a
    // perfectly normal empty response used to render the error state instead of
    // the empty state. That is what broke Settings -> FY Rollover: WF_FYRollover
    // is empty, GetLatestAsync returns null, ASP.NET's HttpNoContentOutputFormatter
    // turns that into 204, and the page reported "data is undefined".
    //
    // null is a valid React Query result and every consumer already handles it
    // (DataBoundary treats null and undefined identically). Returning null here
    // fixes the whole class rather than one page.
    if (response.status === 204) return null as T;

    // Some endpoints return 200 with an empty body (e.g. POST /jobs/{id}/move).
    // response.json() throws SyntaxError on empty body — handle gracefully.
    const text = await response.text();
    if (!text) return null as T;
    return JSON.parse(text);
  }

  async get<T>(path: string): Promise<T> {
    return this.fetch<T>(path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.fetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.fetch<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.fetch<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async del(path: string): Promise<void> {
    return this.fetch<void>(path, { method: 'DELETE' });
  }

  async postFormData<T>(path: string, formData: FormData): Promise<T> {
    const extraHeaders = this.getExtraHeaders();
    const response = await this.sendAuthed(`${this.baseUrl}${path}`, (token) => ({
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
      body: formData,
    }));

    if (!response.ok) {
      throw await this.parseError(response);
    }

    this.unauthorizedHandled = false;

    if (response.status === 204) return undefined as T;

    // Some endpoints return 200 with an empty body (e.g. POST /jobs/{id}/move).
    // response.json() throws SyntaxError on empty body — handle gracefully.
    const text = await response.text();
    if (!text) return undefined as T;
    return JSON.parse(text);
  }

  /** Authenticated GET returning a Blob — for proxied binary downloads (e.g. AML identity-document scans). */
  async getBlob(path: string): Promise<Blob> {
    const extraHeaders = this.getExtraHeaders();
    const response = await this.sendAuthed(`${this.baseUrl}${path}`, (token) => ({
      headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
    }));

    if (!response.ok) {
      throw await this.parseError(response);
    }

    this.unauthorizedHandled = false;
    return response.blob();
  }

  /**
   * As {@link getBlob}, with the response headers — for a download whose headers carry evidence the caller must keep
   * (e.g. the signing document's `X-Document-Sha256`). The API exposes such headers through CORS.
   */
  async getBlobWithHeaders(path: string): Promise<{ blob: Blob; headers: Headers }> {
    const extraHeaders = this.getExtraHeaders();
    const response = await this.sendAuthed(`${this.baseUrl}${path}`, (token) => ({
      headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
    }));

    if (!response.ok) {
      throw await this.parseError(response);
    }

    this.unauthorizedHandled = false;
    return { blob: await response.blob(), headers: response.headers };
  }
}

// Singleton — configured by each portal's main.tsx
let _instance: ApiClient | null = null;

export function configureApiClient(options: ApiClientOptions): void {
  _instance = new ApiClient(options);
}

export const apiClient = {
  get get() { return _instance!.get.bind(_instance!); },
  get post() { return _instance!.post.bind(_instance!); },
  get put() { return _instance!.put.bind(_instance!); },
  get patch() { return _instance!.patch.bind(_instance!); },
  get del() { return _instance!.del.bind(_instance!); },
  get postFormData() { return _instance!.postFormData.bind(_instance!); },
  get getBlob() { return _instance!.getBlob.bind(_instance!); },
  get getBlobWithHeaders() { return _instance!.getBlobWithHeaders.bind(_instance!); },
  get fetch() { return _instance!.fetch.bind(_instance!); },
};
